import { useState } from 'react';
import { useForm } from '@tanstack/react-form';
import type { AnyFieldApi } from '@tanstack/react-form';
import { z } from 'zod';
import { RadixButton as Button, RadixCard as Card, Heading, Text, RadixTextField as TextField } from '@printtat/ui';
import { Link } from 'react-router-dom';
import { Alert } from '@printtat/ui';
import { TurnstileWidget } from '@/shared/components/TurnstileWidget';
import { TURNSTILE_SITE_KEY } from '@/lib/turnstile';

const forgotPasswordSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
});

export const ForgotPasswordPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string>('');

  const form = useForm({
    defaultValues: {
      email: '',
    },
    validators: {
      onChange: forgotPasswordSchema,
    },
    onSubmit: async ({ value }) => {
      if (TURNSTILE_SITE_KEY && !turnstileToken) {
        setSubmitStatus({
          type: 'error',
          message: 'Please complete the captcha before submitting.',
        });
        return;
      }
      setIsSubmitting(true);
      setSubmitStatus(null);

      try {
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000';
        const response = await fetch(`${apiUrl}/auth/forgot-password`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...value,
            ...(turnstileToken
              ? { 'cf-turnstile-response': turnstileToken }
              : {}),
          }),
        });

        const data = await response.json();

        if (response.ok) {
          setSubmitStatus({
            type: 'success',
            message: data.message || 'Password reset instructions have been sent to your email.',
          });
          form.reset();
        } else {
          throw new Error(data.message || 'Failed to send reset email');
        }
      } catch (error) {
        setSubmitStatus({
          type: 'error',
          message: error instanceof Error ? error.message : 'An error occurred. Please try again.',
        });
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-md">
        <Card size="4">
          <div className="text-center mb-8">
            <Heading size="8" weight="bold" className="text-center mb-2">
              Reset Password
            </Heading>
            <Text size="3" color="gray" className="text-center">
              Enter your email and we'll send you instructions to reset your password.
            </Text>
          </div>

          {submitStatus && (
            <Alert variant={submitStatus.type} className="mb-6">
              {submitStatus.message}
            </Alert>
          )}

          {!submitStatus || submitStatus.type === 'error' ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                form.handleSubmit();
              }}
            >
              <div className="space-y-6">
                <form.Field name="email">
                  {(field: AnyFieldApi) => (
                    <div>
                      <label
                        htmlFor={field.name}
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Email Address
                      </label>
                      <TextField.Root
                        id={field.name}
                        name={field.name}
                        type="email"
                        placeholder="you@example.com"
                        value={field.state.value as string}
                        onChange={(e) => field.handleChange(e.target.value)}
                        onBlur={field.handleBlur}
                        disabled={isSubmitting}
                        size="3"
                      />
                      {field.state.meta.errors.length > 0 && (
                        <Text size="1" color="red" className="mt-1 block">
                          {field.state.meta.errors
                            .map((err) =>
                              typeof err === 'string'
                                ? err
                                : (err as { message?: string })?.message ?? '',
                            )
                            .filter(Boolean)
                            .join(', ')}
                        </Text>
                      )}
                    </div>
                  )}
                </form.Field>

                <TurnstileWidget onChange={setTurnstileToken} />

                <Button
                  type="submit"
                  size="3"
                  radius="full"
                  className="w-full"
                  disabled={isSubmitting}
                  loading={isSubmitting}
                >
                  {isSubmitting ? 'Sending...' : 'Send Reset Instructions'}
                </Button>

                <div className="text-center">
                  <Link
                    to="/sign-in"
                    className="text-sm text-blue-600 hover:text-blue-500 font-medium"
                  >
                    Back to Sign In
                  </Link>
                </div>
              </div>
            </form>
          ) : (
            <div className="text-center">
              <Link to="/sign-in">
                <Button size="3" radius="full" variant="soft" className="w-full">
                  Return to Sign In
                </Button>
              </Link>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};
